import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Check, X, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API, getAvailabilityTypeInfo, formatDateDDMMYYYY } from './utils/helpers';
import PermButton from '../shared/PermButton';

const PendingRequestsTab = () => {
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchPendingRequests = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/availability?status=pending`);
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching pending requests:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingRequests();
  }, [fetchPendingRequests]);

  const handleApprove = async (id) => {
    setProcessing(id);
    try {
      await axios.put(`${API}/availability/${id}/approve`);
      toast.success(t('auto.ctr.requestApproved'));
      fetchPendingRequests();
    } catch (error) {
      toast.error(t('auto.ctr.failedToApproveRequest'));
    } finally {
      setProcessing(null);
    }
  };

  const openRejectDialog = (request) => {
    setSelectedRequest(request);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    
    setProcessing(selectedRequest.id);
    try {
      await axios.put(`${API}/availability/${selectedRequest.id}/reject?reason=${encodeURIComponent(rejectReason)}`);
      toast.success(t('auto.ctr.requestRejected'));
      setRejectDialogOpen(false);
      fetchPendingRequests();
    } catch (error) {
      toast.error(t('auto.ctr.failedToRejectRequest'));
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-yellow-600" />
            {t('auto.ctr.pendingAvailabilityRequests')}
          </CardTitle>
          <CardDescription>
            {t('auto.ctr.reviewAndApproveOrRejectEmployee')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Check className="h-12 w-12 mx-auto mb-4 text-green-500" />
              <p>{t('auto.ctr.noPendingRequests')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((request) => (
                <Card key={request.id} className="border-yellow-200 bg-yellow-50/50">
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-start sm:justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-lg break-words">{request.employee_name}</p>
                        <p className="text-sm text-gray-600">
                          {formatDateDDMMYYYY(request.start_date)} - {formatDateDDMMYYYY(request.end_date)}
                        </p>
                        <Badge className={getAvailabilityTypeInfo(request.availability_type).color + ' mt-2'}>
                          {getAvailabilityTypeInfo(request.availability_type).label}
                        </Badge>
                        {request.reason && (
                          <p className="text-sm text-gray-500 mt-2 break-words">{request.reason}</p>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 shrink-0">
                        <PermButton
                          perm="manpower.approve"
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleApprove(request.id)}
                          disabled={processing === request.id}
                          data-testid={`approve-request-${request.id}`}
                        >
                          <Check className="h-4 w-4 mr-1" /> {t('auto.ctr.approve')}
                        </PermButton>
                        <PermButton
                          perm="manpower.approve"
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-600 hover:bg-red-50"
                          onClick={() => openRejectDialog(request)}
                          disabled={processing === request.id}
                          data-testid={`reject-request-${request.id}`}
                        >
                          <X className="h-4 w-4 mr-1" /> {t('auto.ctr.reject')}
                        </PermButton>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('auto.ctr.rejectRequest')}</DialogTitle>
            <DialogDescription>
              {t('auto.ctr.optionallyProvideAReasonForRejecting')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder={t('auto.ctr.reasonForRejectionOptional')}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)} data-testid="dialog-cancel-btn">{t('auto.ctr.cancel')}</Button>
            <Button variant="destructive" onClick={handleReject} disabled={processing} data-testid="dialog-reject-request-btn">
              {t('auto.ctr.rejectRequest')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PendingRequestsTab;
